import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

const serviceDropdown = [
  { label: "Daire Temizliği", path: "/hizmetlerimiz/daire-temizligi" },
  { label: "Bina ve Site Temizliği", path: "/hizmetlerimiz/bina-ve-site-temizligi" },
  { label: "Apartman Temizliği", path: "/hizmetlerimiz/apartman-temizligi" },
  { label: "Profesyonel Yönetim", path: "/hizmetlerimiz/profesyonel-apartman-ve-site-yonetimi" },
  { label: "İnşaat Sonrası Temizlik", path: "/hizmetlerimiz/insaat-sonrasi-temizlik" },
  { label: "Koltuk Yıkama", path: "/hizmetlerimiz/koltuk-yikama" },
  { label: "Halı Yıkama", path: "/hizmetlerimiz/hali-yikama" },
  { label: "Kuru Temizleme ve Ütü", path: "/hizmetlerimiz/kuru-temizleme-ve-utu" },
  { label: "İlaçlama", path: "/hizmetlerimiz/ilaclama" },
  { label: "Ofis Temizliği", path: "/hizmetlerimiz/ofis-ve-is-yeri-temizligi" },
];

const regionDropdown = [
  { label: "Sarıçam", path: "/hizmet-bolgelerimiz/saricam" },
  { label: "Çukurova", path: "/hizmet-bolgelerimiz/cukurova" },
  { label: "Seyhan", path: "/hizmet-bolgelerimiz/seyhan" },
  { label: "Yüreğir", path: "/hizmet-bolgelerimiz/yuregir" },
];

function isActive(path: string, current: string) {
  if (path === "/") return current === "/";
  return current.startsWith(path);
}

function NavDivider() {
  return <div className="hidden xl:block w-px h-5 bg-[#0A2F63]/15 self-center" />;
}

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileSubmenu, setMobileSubmenu] = useState<string | null>(null);
  const location = useLocation();
  const dropdownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setMenuOpen(false);
    setMobileSubmenu(null);
    setOpenDropdown(null);
  }, [location.pathname]);

  const handleDropdownEnter = (name: string) => {
    if (dropdownTimer.current) clearTimeout(dropdownTimer.current);
    setOpenDropdown(name);
  };

  const handleDropdownLeave = () => {
    dropdownTimer.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 200);
  };

  const isServiceActive = serviceDropdown.some((s) => isActive(s.path, location.pathname));
  const isRegionActive = regionDropdown.some((r) => isActive(r.path, location.pathname));

  const navBase = "font-sans text-[14px] font-medium uppercase tracking-wider text-[#0A2F63] transition-colors duration-200";
  const navHover = "hover:text-[#2EA44F]";
  const navActive = "text-[#2EA44F]";
  const navItemClass = `px-3 py-2.5 rounded-lg ${navBase} ${navHover}`;
  const activeItemClass = `px-3 py-2.5 rounded-lg ${navBase} ${navActive}`;

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100/80 h-[72px] md:h-[82px]">
        <div className="w-full h-full px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between h-full">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 shrink-0 group">
              <img
                src="/assets/images/logo.png"
                alt="Day Temizlik Adana Logo"
                className="h-10 md:h-12 w-auto transition-transform duration-300 group-hover:scale-105"
              />
              <span className="font-sans text-lg md:text-xl font-bold text-[#0A2F63] hidden sm:block">
                Day Temizlik Adana
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-0.5">
              <Link to="/" className={isActive("/", location.pathname) ? activeItemClass : navItemClass}>
                Anasayfa
              </Link>
              <NavDivider />
              <Link to="/kurumsal" className={isActive("/kurumsal", location.pathname) ? activeItemClass : navItemClass}>
                Kurumsal
              </Link>
              <NavDivider />
              <div className="relative" onMouseEnter={() => handleDropdownEnter("services")} onMouseLeave={handleDropdownLeave}>
                <button className={`flex items-center gap-1.5 ${isServiceActive || isActive("/hizmetlerimiz", location.pathname) ? activeItemClass : navItemClass}`}>
                  Hizmetlerimiz
                  <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${openDropdown === "services" ? "rotate-180" : ""}`}></i>
                </button>
                {openDropdown === "services" && (
                  <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl border border-gray-100 shadow-xl shadow-black/5 dropdown-enter overflow-hidden">
                    <div className="py-2">
                      <Link to="/hizmetlerimiz" className="block px-4 py-2.5 text-[14px] font-semibold text-[#2EA44F] hover:bg-gray-50 transition-colors">
                        Tüm Hizmetlerimiz
                      </Link>
                      <div className="mx-4 my-1.5 border-t border-gray-100"></div>
                      {serviceDropdown.map((item) => (
                        <Link key={item.path} to={item.path} className={`block px-4 py-2 text-[14px] uppercase tracking-wider transition-colors ${isActive(item.path, location.pathname) ? "text-[#2EA44F] bg-gray-50 font-medium" : "text-[#0A2F63] hover:text-[#2EA44F] hover:bg-gray-50"}`}>
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <NavDivider />
              <div className="relative" onMouseEnter={() => handleDropdownEnter("regions")} onMouseLeave={handleDropdownLeave}>
                <button className={`flex items-center gap-1.5 ${isRegionActive || isActive("/hizmet-bolgelerimiz", location.pathname) ? activeItemClass : navItemClass}`}>
                  Hizmet Bölgelerimiz
                  <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${openDropdown === "regions" ? "rotate-180" : ""}`}></i>
                </button>
                {openDropdown === "regions" && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl border border-gray-100 shadow-xl shadow-black/5 dropdown-enter overflow-hidden">
                    <div className="py-2">
                      <Link to="/hizmet-bolgelerimiz" className="block px-4 py-2.5 text-[14px] font-semibold text-[#2EA44F] hover:bg-gray-50 transition-colors">
                        Tüm Bölgelerimiz
                      </Link>
                      <div className="mx-4 my-1.5 border-t border-gray-100"></div>
                      {regionDropdown.map((item) => (
                        <Link key={item.path} to={item.path} className={`block px-4 py-2 text-[14px] uppercase tracking-wider transition-colors ${isActive(item.path, location.pathname) ? "text-[#2EA44F] bg-gray-50 font-medium" : "text-[#0A2F63] hover:text-[#2EA44F] hover:bg-gray-50"}`}>
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <NavDivider />
              <Link to="/sikca-sorulan-sorular" className={isActive("/sikca-sorulan-sorular", location.pathname) ? activeItemClass : navItemClass}>
                S.S.S.
              </Link>
              <NavDivider />
              <Link to="/iletisim" className={isActive("/iletisim", location.pathname) ? activeItemClass : navItemClass}>
                İletişim
              </Link>
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              <a href="tel:05539591940" className="hidden md:flex items-center gap-2 px-5 py-2.5 rounded-full text-[14px] font-semibold whitespace-nowrap bg-[#0A2F63] text-white hover:bg-[#0A2F63]/90 transition-all duration-300 shadow-sm">
                <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                0553 959 19 40
              </a>
              <a href="https://wa.me/905539591940" target="_blank" rel="noopener noreferrer" className="hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-[#2EA44F] text-white hover:bg-[#2EA44F]/90 transition-all duration-300 hover:scale-105 shadow-sm" aria-label="WhatsApp">
                <i className="ri-whatsapp-line w-5 h-5 flex items-center justify-center"></i>
              </a>
              <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg text-[#0A2F63] hover:bg-gray-50 transition-colors" aria-label="Menüyü aç/kapat">
                <i className={`ri-${menuOpen ? "close" : "menu-3"}-line w-5 h-5 flex items-center justify-center text-xl`}></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Full-Screen Menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden mobile-menu-enter">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setMenuOpen(false)}></div>
          <div className="absolute top-0 right-0 h-full w-full max-w-sm bg-white shadow-2xl overflow-y-auto">
            <div className="p-6 pt-20">
              {/* Simple Links */}
              <div className="space-y-1 mb-4">
                {[
                  { label: "Anasayfa", path: "/", icon: "ri-home-line" },
                  { label: "Kurumsal", path: "/kurumsal", icon: "ri-building-line" },
                  { label: "S.S.S.", path: "/sikca-sorulan-sorular", icon: "ri-questionnaire-line" },
                  { label: "İletişim", path: "/iletisim", icon: "ri-contacts-line" },
                ].map((link) => (
                  <Link key={link.path} to={link.path} className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-[14px] font-medium uppercase tracking-wider transition-colors ${isActive(link.path, location.pathname) ? "text-[#2EA44F] bg-gray-50" : "text-[#0A2F63] hover:bg-gray-50"}`}>
                    <i className={`${link.icon} w-5 h-5 flex items-center justify-center text-[#2EA44F]`}></i>
                    {link.label}
                  </Link>
                ))}
              </div>

              {/* Hizmetlerimiz Accordion */}
              <div className="mb-2">
                <button onClick={() => setMobileSubmenu(mobileSubmenu === "services" ? null : "services")} className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl text-[14px] font-medium uppercase tracking-wider transition-colors ${isServiceActive || isActive("/hizmetlerimiz", location.pathname) ? "text-[#2EA44F] bg-gray-50" : "text-[#0A2F63] hover:bg-gray-50"}`}>
                  <div className="flex items-center gap-3">
                    <i className="ri-sparkling-line w-5 h-5 flex items-center justify-center text-[#2EA44F]"></i>
                    Hizmetlerimiz
                  </div>
                  <i className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileSubmenu === "services" ? "rotate-180" : ""}`}></i>
                </button>
                {mobileSubmenu === "services" && (
                  <div className="mt-1 ml-4 pl-4 border-l-2 border-gray-100 space-y-1">
                    <Link to="/hizmetlerimiz" className="block px-3 py-2.5 rounded-lg text-[14px] font-semibold text-[#2EA44F] hover:bg-gray-50 transition-colors">Tüm Hizmetler</Link>
                    {serviceDropdown.map((item) => (
                      <Link key={item.path} to={item.path} className={`block px-3 py-2.5 rounded-lg text-[14px] transition-colors ${isActive(item.path, location.pathname) ? "text-[#2EA44F] bg-gray-50 font-medium" : "text-[#0A2F63]/80 hover:text-[#2EA44F] hover:bg-gray-50"}`}>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Hizmet Bölgelerimiz Accordion */}
              <div className="mb-4">
                <button onClick={() => setMobileSubmenu(mobileSubmenu === "regions" ? null : "regions")} className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl text-[14px] font-medium uppercase tracking-wider transition-colors ${isRegionActive || isActive("/hizmet-bolgelerimiz", location.pathname) ? "text-[#2EA44F] bg-gray-50" : "text-[#0A2F63] hover:bg-gray-50"}`}>
                  <div className="flex items-center gap-3">
                    <i className="ri-map-pin-line w-5 h-5 flex items-center justify-center text-[#2EA44F]"></i>
                    Hizmet Bölgelerimiz
                  </div>
                  <i className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileSubmenu === "regions" ? "rotate-180" : ""}`}></i>
                </button>
                {mobileSubmenu === "regions" && (
                  <div className="mt-1 ml-4 pl-4 border-l-2 border-gray-100 space-y-1">
                    <Link to="/hizmet-bolgelerimiz" className="block px-3 py-2.5 rounded-lg text-[14px] font-semibold text-[#2EA44F] hover:bg-gray-50 transition-colors">Tüm Bölgeler</Link>
                    {regionDropdown.map((item) => (
                      <Link key={item.path} to={item.path} className={`block px-3 py-2.5 rounded-lg text-[14px] transition-colors ${isActive(item.path, location.pathname) ? "text-[#2EA44F] bg-gray-50 font-medium" : "text-[#0A2F63]/80 hover:text-[#2EA44F] hover:bg-gray-50"}`}>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3 pt-4 border-t border-gray-100">
                <a href="tel:05539591940" className="flex items-center justify-center gap-2 w-full bg-[#0A2F63] text-white py-3.5 rounded-xl text-[14px] font-semibold hover:bg-[#0A2F63]/90 transition-colors">
                  <i className="ri-phone-line w-5 h-5 flex items-center justify-center"></i>
                  0553 959 19 40
                </a>
                <a href="https://wa.me/905539591940" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-[#2EA44F] text-white py-3.5 rounded-xl text-[14px] font-semibold hover:bg-[#2EA44F]/90 transition-colors">
                  <i className="ri-whatsapp-line w-5 h-5 flex items-center justify-center"></i>
                  WhatsApp ile Yazın
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}